/* eslint-disable no-unused-vars */
import React, {useState, useRef} from 'react';
import {useForm, FormProvider, SubmitHandler} from 'react-hook-form';
import EmailEditor, {EditorRef, EmailEditorProps} from 'react-email-editor';
import {collection, doc, setDoc, getDocs} from 'firebase/firestore';
import {useNavigate} from 'react-router-dom';
import {db, auth} from '../../../firebase';
import {toast} from 'react-toastify';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import TabList from '@mui/lab/TabList';

interface ValuesProps {
  type: string,
  title: string,
  author: string,
  status: string,
  version: string,
  labels: [string],
  deployDate: Date,
  versionDate: Date,
  commitHash: string,
  htmlCompact: string,
  htmlComplete: string,
  versionDescription: string,
}

const ReleasesCreation = () => {
  const navigate = useNavigate();
  const emailEditorRef = useRef<EditorRef>(null);
  const [tabHtml, setTabHtml] = useState('1');
  const [htmlCompact, setHtmlCompact] = useState<any>();
  const [htmlComplete, setHtmlComplete] = useState<any>();

  const methods = useForm<ValuesProps>({
    defaultValues: {
      title: '',
      labels: [],
      version: '',
      commitHash: '',
      deployDate: undefined,
      versionDate: undefined,
      versionDescription: '',
      type: 'hidden',
      status: 'notPosted',
      author: auth?.currentUser?.email ?? '',
    },
  });

  const {
    watch,
    register,
    formState: {errors},
  } = methods;

  const [type] = watch(['type']);

  const onReady: EmailEditorProps['onReady'] = (unlayer) => {
    unlayer.setAppearance({
      theme: 'dark',
    });

    unlayer.loadDesign(tabHtml === '1' ? htmlCompact?.design : htmlComplete?.design);

    unlayer.addEventListener('design:updated', () => {
      unlayer.exportHtml(function(data: any) {
        if (tabHtml === '1') {
          setHtmlCompact(data);
        } else {
          setHtmlComplete(data);
        }
      });
    });

    // unlayer.registerCallback('image', function(file, done) {
    //   var data = new FormData()
    //   data.append('file', file.attachments[0])

    //   fetch('/uploads', {
    //     method: 'POST',
    //     headers: {
    //       'Accept': 'application/json'
    //     },
    //     body: data
    //   }).then(response => {
    //     // Make sure the response was valid
    //     if (response.status >= 200 && response.status < 300) {
    //       return response
    //     } else {
    //       var error = new Error(response.statusText)
    //       error.response = response
    //       throw error
    //     }
    //   }).then(response => {
    //     return response.json()
    //   }).then(data => {
    //     // Pass the URL back to Unlayer to mark this upload as completed
    //     done({ progress: 100, url: data.filelink })
    //   })
    // })
  };

  const onSubmit: SubmitHandler<ValuesProps> = async (data) => {
    const collectionRef = collection(db, 'releasesCreated');
    if (data.type === 'hidden') {
      data = {
        ...data,
        htmlCompact: htmlCompact.html,
      };
    } else {
      data = {
        ...data,
        htmlCompact: htmlCompact.html,
        htmlComplete: htmlComplete.html,
      };
    };

    await setDoc(doc(collectionRef), {
      ...data,
    }).then(() => {
      toast.success('Release Criado', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });

      if (data.type === 'hidden') {
        navigate('/telemedicine/releases');
      } else {
        sendEmailToClients(data);
      }
    });
  };

  const sendEmailToClients = async (data: ValuesProps) => {
    const collectionMailRef = collection(db, 'mail');
    const queryCustomerEmailsRef = await getDocs(collection(db, 'customerEmails'));

    queryCustomerEmailsRef.forEach((docs: any) => {
      const emails = docs.data().emails;
      if (emails.length === 0) {
        toast.error(`Não existe emails cadastrados para o cliente ${docs.data().name}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
        });
      } else {
        emails.forEach((email: string) => {
          setDoc(doc(collectionMailRef), {
            to: email,
            message: {
              subject: data.title,
              html: data.htmlComplete,
            },
          }).catch((err) => {
            console.log(`Erro: ${err}`);

            toast.error(`Não foi possível enviar para o email ${email}`, {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: false,
              progress: undefined,
            });
          });
        });
      }
      navigate('/telemedicine/releases');
    });
  };

  return (
    <Grid
      container
      spacing={4}
      style={{
        padding: '1em 2em 2em 2em',
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h4">
        Criação de Release
        </Typography>
      </Grid>
      <FormProvider {...methods}>
        <Grid item xs={2}>
          <TextField
            fullWidth
            label="Título"
            variant="outlined"
            InputLabelProps={{shrink: true}}
            {
              ...register('title', {
                required: true,
              })
            }
            error={errors?.title ? true : false}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            fullWidth
            label="Versão"
            variant="outlined"
            InputLabelProps={{shrink: true}}
            {
              ...register('version', {
                required: true,
              })
            }
            error={errors?.version ? true : false}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            disabled
            fullWidth
            label="Autor"
            value={auth?.currentUser?.email ?? ''}
            variant="outlined"
            InputLabelProps={{shrink: true}}
            {
              ...register('author')
            }
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl variant='outlined' style={{width: '100%'}}>
            <TextField
              select
              fullWidth
              label="Tipo"
              defaultValue={'hidden'}
              InputLabelProps={{shrink: true}}
              {
                ...register('type', {
                  onChange: (e) => {
                    if (e.target.value === 'hidden') setTabHtml('1');
                  }})
              }
            >
              <MenuItem value={'feature'}>Feature</MenuItem>
              <MenuItem value={'hotfix'}>Hotfix</MenuItem>
              <MenuItem value={'hidden'}>Hidden</MenuItem>
            </TextField>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <Autocomplete
            multiple
            freeSolo
            clearOnBlur
            options={[]}
            limitTags={2}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Labels"
                variant="outlined"
                InputLabelProps={{shrink: true}}
                {
                  ...register('labels')
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            fullWidth
            type="date"
            variant="outlined"
            label="Data da Versão"
            InputLabelProps={{shrink: true}}
            {
              ...register('versionDate', {
                required: true,
              })
            }
            error={errors?.versionDate ? true : false}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            fullWidth
            type="date"
            label="Data do Deploy"
            InputLabelProps={{shrink: true}}
            {
              ...register('deployDate')
            }
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Descrição da Versão"
            variant="outlined"
            InputLabelProps={{shrink: true}}
            {
              ...register('versionDescription')
            }
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Hash do Commit"
            variant="outlined"
            InputLabelProps={{shrink: true}}
            {
              ...register('commitHash', {
                required: true,
              })
            }
            error={errors?.commitHash ? true : false}
          />
        </Grid>
        <Grid item xs={2}>
          <FormControl variant='outlined' style={{width: '100%'}}>
            <TextField
              select
              disabled
              fullWidth
              label="Status"
              defaultValue={'notPosted'}
              InputLabelProps={{shrink: true}}
              {
                ...register('status')
              }
            >
              <MenuItem value={'notPosted'}>Não Postado</MenuItem>
              <MenuItem value={'posted'}>Postado</MenuItem>
            </TextField>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TabContext value={tabHtml}>
            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
              <TabList value={tabHtml} onChange={() => setTabHtml(tabHtml === '1' ? '2' : '1')}>
                <Tab label='Html Resumo' value="1"/>
                {type !== 'hidden' && (
                  <Tab label='Html Completo' value="2"/>
                )}
              </TabList>
            </Box>
            <TabPanel value="1">
              <Grid item xs={12}>
                <Typography variant="h5">
                  Html Resumo
                </Typography>
                <Typography variant="caption">
                  Iframe que estará dentro da plataforma
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <EmailEditor
                  ref={emailEditorRef}
                  onReady={onReady}
                />
              </Grid>
            </TabPanel>
            <TabPanel value="2">
              <Grid item xs={12}>
                <Typography variant="h5">
                  Html Completo
                </Typography>
                <Typography variant="caption">
                  Email que será enviado para os clientes
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <EmailEditor
                  ref={emailEditorRef}
                  onReady={onReady}
                />
              </Grid>
            </TabPanel>
          </TabContext>
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Button variant="outlined" type="submit">
              Salvar e enviar
            </Button>
          </form>
        </Grid>
      </FormProvider>
    </Grid>

  );
};

export default ReleasesCreation;
